/**
 * CampaignInsightsCard – Card de insights pós-campanha gerados por IA.
 * Mostra análise, pontos fortes, fracos e recomendações.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2, TrendingUp, TrendingDown, Lightbulb, ArrowRight } from "lucide-react";

interface CampaignInsightsCardProps {
  campaignName: string;
  stats: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    unsubscribed: number;
  };
}

type InsightsResult = {
  summary: string;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
  nextSteps: string[];
};

export default function CampaignInsightsCard({ campaignName, stats }: CampaignInsightsCardProps) {
  const [result, setResult] = useState<InsightsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const insightsMutation = trpc.aiEmail.analyzeContent.useMutation();

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    try {
      const openRate = stats.delivered > 0 ? ((stats.opened / stats.delivered) * 100).toFixed(1) : "0";
      const clickRate = stats.delivered > 0 ? ((stats.clicked / stats.delivered) * 100).toFixed(1) : "0";

      // Build plain-text stats (no HTML interpolation to avoid XSS)
      const statsText = [
        `Enviados: ${stats.sent}`,
        `Entregues: ${stats.delivered}`,
        `Abertos: ${stats.opened} (${openRate}%)`,
        `Clicados: ${stats.clicked} (${clickRate}%)`,
        `Bounce: ${stats.bounced}`,
        `Descadastros: ${stats.unsubscribed}`,
      ].join(". ");

      const analysis = await insightsMutation.mutateAsync({
        html: `<p>${statsText}</p>`,
        subject: campaignName,
      });

      setResult({
        summary: `Taxa de abertura: ${openRate}%. Taxa de clique: ${clickRate}%.`,
        strengths: analysis.persuasion?.strengths ?? [],
        weaknesses: analysis.persuasion?.weaknesses ?? [],
        recommendations: analysis.improvements ?? [],
        nextSteps: analysis.readability?.suggestions ?? [],
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }

  if (!result) {
    return (
      <Card className="p-4 border-indigo-200 bg-indigo-50/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-indigo-700">Insights IA</span>
          </div>
          <Button onClick={handleGenerate} disabled={loading} size="sm" variant="outline">
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Analisando...</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-2" /> Gerar Insights</>
            )}
          </Button>
        </div>
        {error && <p className="text-xs text-destructive mt-2">{error}</p>}
      </Card>
    );
  }

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        <span className="text-sm font-semibold">Insights IA</span>
      </div>

      <p className="text-sm text-muted-foreground">{result.summary}</p>

      {result.strengths.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-emerald-700 flex items-center gap-1 mb-1">
            <TrendingUp className="w-3.5 h-3.5" /> Pontos Fortes
          </h4>
          <ul className="space-y-1">
            {result.strengths.map((s, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span className="text-emerald-500 mt-0.5">+</span> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.weaknesses.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1">
            <TrendingDown className="w-3.5 h-3.5" /> Pontos a Melhorar
          </h4>
          <ul className="space-y-1">
            {result.weaknesses.map((w, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">-</span> {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recommendations.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-indigo-700 flex items-center gap-1 mb-1">
            <Lightbulb className="w-3.5 h-3.5" /> Recomendações
          </h4>
          <ul className="space-y-1">
            {result.recommendations.map((r, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <ArrowRight className="w-3 h-3 text-indigo-400 mt-0.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button onClick={handleGenerate} disabled={loading} size="sm" variant="ghost" className="w-full text-xs">
        Re-analisar
      </Button>
    </Card>
  );
}
